import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WholesaleLayout, { useWholesaleAuth, StatusBadge } from './WholesaleLayout';
import { getWholesaleOrders, getWholesaleProducts, wholesaleOrderAction } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WholesaleAdminOrders() {
  const user = useWholesaleAuth('admin');
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [trackingInputs, setTrackingInputs] = useState({});

  const load = () => {
    if (!user) return;
    getWholesaleOrders().then(r => setOrders(r.data)).catch(() => {});
    getWholesaleProducts().then(r => setProducts(r.data)).catch(() => {});
  };
  useEffect(load, [user]);

  const token = localStorage.getItem('auth_token');
  const stockMap = {};
  products.forEach(p => { stockMap[p.id] = p.stock ?? 0; });

  const doAction = async (oid, action, extra = {}) => {
    try {
      await wholesaleOrderAction(oid, { action, ...extra });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Action failed'); }
  };

  return (
    <WholesaleLayout user={user} title="All Orders">
      {orders.length === 0 && <div style={{ color:'#888', fontSize:14, padding:40, textAlign:'center' }}>No orders</div>}
      {orders.map(o => (
        <div key={o.id} data-testid={`admin-order-${o.id}`} style={{ background:'#fff', border:'1px solid #E0DED8', borderRadius:4, marginBottom:16, overflow:'hidden' }}>
          <div style={{ padding:'20px 24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:700, letterSpacing:1 }}>{o.id}</span>
                {o.admin_edited && <span style={{ padding:'2px 8px', background:'#FFF3E0', color:'#E65100', fontSize:10, fontWeight:600, borderRadius:2, letterSpacing:1 }}>EDITED</span>}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:12, color:'#888' }}>{o.wholesaler_name} &middot; {new Date(o.created_at).toLocaleDateString()}</span>
                <StatusBadge status={o.status} />
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:20, fontWeight:700 }}>${o.total?.toLocaleString()}</span>
              </div>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr>{['Item','Price','Qty','HSN','Total'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {(o.items || []).map((it, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>
                      {it.name}
                      {it.id && stockMap[it.id] !== undefined && <span style={{ fontSize:11, color:'#888', marginLeft:6 }}>(stock: {stockMap[it.id]})</span>}
                    </td>
                    <td style={tdStyle}>${Number(it.price).toLocaleString()}</td>
                    <td style={tdStyle}>{it.qty}</td>
                    <td style={tdStyle}>{it.hsn}</td>
                    <td style={tdStyle}>${(it.price * it.qty).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {o.notes && <div style={{ marginTop:12, padding:'10px 14px', background:'#F7F6F2', borderRadius:2, fontSize:13, color:'#555' }}>{o.notes}</div>}
            {o.edit_note && <div style={{ marginTop:8, padding:'10px 14px', background:'#FFF3E0', borderRadius:2, fontSize:13, color:'#E65100' }}>Edit note: {o.edit_note}</div>}
            {o.pi_number && <div style={{ marginTop:8, fontSize:11, color:'#888' }}>PI: {o.pi_number}</div>}
            {o.ci_number && <div style={{ fontSize:11, color:'#888' }}>CI: {o.ci_number}</div>}
          </div>
          <div style={{ borderTop:'1px solid #E0DED8', padding:'14px 24px', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            {o.status === 'pending' && <>
              <button onClick={() => doAction(o.id, 'approve')} style={{ ...btn, background:'#2E7D32', color:'#fff' }}>Approve</button>
              <button onClick={() => doAction(o.id, 'reject')} style={{ ...btn, background:'#FCE4EC', color:'#B71C1C' }}>Reject</button>
            </>}
            {o.status === 'approved' && <button onClick={() => doAction(o.id, 'mark_paid')} style={{ ...btn, background:'#1565C0', color:'#fff' }}>Mark Paid</button>}
            {['approved','paid','shipped'].includes(o.status) && <>
              <input placeholder="Tracking number" value={trackingInputs[o.id] || o.tracking || ''} onChange={e => setTrackingInputs(p => ({ ...p, [o.id]: e.target.value }))} style={{ width:220, padding:'8px 12px', border:'1px solid #E0DED8', borderRadius:2, fontFamily:'Barlow', fontSize:12 }} />
              <button onClick={() => doAction(o.id, 'ship', { tracking: trackingInputs[o.id] || o.tracking || '' })} style={{ ...btn, background:'#111', color:'#fff' }}>Ship + Save</button>
            </>}
            <button onClick={() => navigate(`/wholesale/admin/order/${o.id}/edit`)} style={{ ...btn, background:'transparent', color:'#111', border:'1.5px solid #111' }}>Edit Order</button>
            {['approved','paid','shipped'].includes(o.status) && <>
              <a href={`${API}/api/wholesale/invoice/pi/${o.id}?token=${token}`} target="_blank" rel="noreferrer" style={{ ...btn, background:'transparent', color:'#111', border:'1.5px solid #111', textDecoration:'none' }}>PI</a>
              <a href={`${API}/api/wholesale/invoice/ci/${o.id}?token=${token}`} target="_blank" rel="noreferrer" style={{ ...btn, background:'transparent', color:'#C41E2A', border:'1.5px solid #C41E2A', textDecoration:'none' }}>CI</a>
            </>}
          </div>
        </div>
      ))}
    </WholesaleLayout>
  );
}

const thStyle = { padding:'10px 12px', fontSize:10, letterSpacing:1.5, textTransform:'uppercase', color:'#888', borderBottom:'2px solid #E0DED8', fontWeight:600, textAlign:'left' };
const tdStyle = { padding:'12px', borderBottom:'1px solid #E0DED8', verticalAlign:'middle' };
const btn = { display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', border:'none', transition:'all 0.15s' };
